import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function HeroSection() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <section className="relative pt-24 md:pt-32 pb-16 md:pb-20 overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-0 w-[600px] h-[600px] rounded-full bg-primary-100/30 blur-3xl"></div>
        <div className="absolute bottom-10 left-[-100px] w-[400px] h-[400px] rounded-full bg-accent-100/20 blur-3xl"></div>
        <div className="absolute top-1/2 left-1/3 w-[300px] h-[300px] rounded-full bg-secondary-100/30 blur-3xl"></div>
      </div>

      <div className="container-custom relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {/* Left Content */}
          <div className="lg:col-span-5">
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground-900 leading-tight mb-6">
              OOPBUY Spreadsheet &ndash; Die ultimative deutsche Produkt&uuml;bersicht
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-8 max-w-lg">
              Finde tausende Produkte schneller. Vergleiche Kategorien, verstehe Einkaufsprozesse und entdecke die besten Methoden f&uuml;r deine OOPBUY Bestellung.
            </p>

            {/* Category Dropdown */}
            <div ref={dropdownRef} className="relative mb-6">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-full sm:w-auto flex items-center justify-between gap-3 px-5 py-3 bg-background-100 border border-background-200/70 rounded-xl text-sm font-medium text-foreground-800 hover:border-background-300 transition-colors cursor-pointer"
              >
                <span>Kategorie w&auml;hlen</span>
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line"></i>
                </span>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-background-50 rounded-xl border border-background-200/70 py-2 shadow-lg z-20 max-h-80 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors whitespace-nowrap"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                oopbuy spreadsheet
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line"></i>
                </span>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-foreground-900 text-background-50 rounded-full text-sm font-semibold hover:bg-foreground-800 transition-all whitespace-nowrap cursor-pointer"
              >
                buy it now
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line"></i>
                </span>
              </a>
            </div>

            {/* Trust elements */}
            <div className="flex flex-wrap gap-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                  <i className="ri-check-line"></i>
                </span>
                Deutsche Erkl&auml;rung
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                  <i className="ri-check-line"></i>
                </span>
                Schritt-f&uuml;r-Schritt Guides
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                  <i className="ri-check-line"></i>
                </span>
                Sicherheitsinformationen
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                  <i className="ri-check-line"></i>
                </span>
                Produktvergleich
              </span>
            </div>
          </div>

          {/* Center Image */}
          <div className="lg:col-span-4 flex justify-center">
            <div className="w-full max-w-[380px] h-[480px] rounded-3xl overflow-hidden bg-background-100">
              <img
                src="https://readdy.ai/api/search-image?query=Curated%20fashion%20flat%20lay%20with%20white%20sneakers%20folded%20denim%20and%20minimalist%20watch%20on%20warm%20cream%20linen%20surface%2C%20soft%20afternoon%20sunlight%20casting%20gentle%20shadows%2C%20editorial%20product%20photography%2C%20refined%20contemporary%20aesthetic%2C%20neutral%20beige%20and%20ivory%20palette%2C%20premium%20lifestyle%20composition%2C%20high-end%20catalog%20style&width=800&height=1000&seq=hero-main-v2-01&orientation=portrait"
                alt="OOPBUY Spreadsheet Produkt&uuml;bersicht"
                title="OOPBUY Spreadsheet Deutschland 2026"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Right Side Cards */}
          <div className="lg:col-span-3">
            <div className="flex items-center gap-2 mb-4">
              <p className="text-sm font-semibold text-foreground-800">Beliebte Kategorien</p>
              <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                <i className="ri-arrow-right-up-line"></i>
              </span>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div className="relative rounded-2xl overflow-hidden h-44 bg-background-100">
                <img
                  src="https://readdy.ai/api/search-image?query=Luxury%20leather%20loafers%20in%20cognac%20brown%20on%20textured%20cream%20paper%20background%2C%20soft%20directional%20window%20light%2C%20editorial%20product%20photography%2C%20rich%20warm%20tones%2C%20sophisticated%20footwear%20presentation%2C%20premium%20craftsmanship%20details%20visible%2C%20elegant%20minimal%20composition&width=600&height=400&seq=hero-side-v2-01&orientation=landscape"
                  alt="Shoes Kategorie"
                  title="OOPBUY Spreadsheet Shoes Kategorie"
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-3 left-3 px-3 py-1 bg-background-50/90 backdrop-blur-sm rounded-full">
                  <span className="text-xs font-medium text-foreground-800">Shoes</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="relative rounded-2xl overflow-hidden h-32 bg-background-100">
                  <img
                    src="https://readdy.ai/api/search-image?query=Textured%20knit%20cardigan%20in%20warm%20sand%20color%20draped%20on%20light%20oak%20surface%2C%20soft%20morning%20light%20through%20sheer%20curtain%2C%20minimal%20product%20photography%2C%20cozy%20sophisticated%20aesthetic%2C%20fine%20textile%20details%2C%20editorial%20catalog%20style%2C%20inviting%20warm%20neutral%20tones&width=400&height=300&seq=hero-side-v2-02&orientation=landscape"
                    alt="Hoodies Kategorie"
                    title="OOPBUY Spreadsheet Hoodies Kategorie"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-2 left-2 px-2.5 py-0.5 bg-background-50/90 backdrop-blur-sm rounded-full">
                    <span className="text-[10px] font-medium text-foreground-800">Hoodies</span>
                  </div>
                </div>
                <div className="relative rounded-2xl overflow-hidden h-32 bg-background-100">
                  <img
                    src="https://readdy.ai/api/search-image?query=Structured%20canvas%20tote%20bag%20with%20leather%20trim%20in%20oatmeal%20tone%20on%20cream%20marble%20surface%2C%20soft%20diffused%20studio%20light%2C%20minimal%20product%20photography%2C%20refined%20casual%20luxury%20aesthetic%2C%20clean%20architectural%20composition%2C%20premium%20accessory%20presentation&width=400&height=300&seq=hero-side-v2-03&orientation=landscape"
                    alt="Bags Kategorie"
                    title="OOPBUY Spreadsheet Bags Kategorie"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-2 left-2 px-2.5 py-0.5 bg-background-50/90 backdrop-blur-sm rounded-full">
                    <span className="text-[10px] font-medium text-foreground-800">Bags</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}